import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { selectCurrentEventId } from "modules/event/selectors";
import useRelationshipsTracking from "modules/eventUserPresence/hooks/useRelationshipsTracking";
import { selectCurrentRoomId } from "store/rooms";
import logger from "logging/logger";
import { usePrevious } from "helpers/reactHooksUtils";
import { useUserSession } from "./hooks/userSession";
import { useConnectionAttempts } from "./hooks/useConnectionAttempts/useConnectionAttempts";

export const USER_IN_EVENT_INTERVAL_MS = 30 * 1000;
export const DISCONNECT_TIMEOUT_MS = 5 * 1000;
export const MAX_CONNECTION_ATTEMPTS = 3;

const EventUserPresenceTracker = () => {
  const eventId = useSelector(selectCurrentEventId);
  const floorId = useSelector(selectCurrentSpaceId);
  const tableId = useSelector(selectCurrentRoomId);
  const { createSession } = useUserSession();
  const { disconnect, incrementConnectionAttempts, resetConnectionAttempts } =
    useConnectionAttempts(MAX_CONNECTION_ATTEMPTS);
  useRelationshipsTracking();
  const disconnectionTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const timerInterval = window.setInterval(async () => {
      if (!eventId || !floorId || !tableId) {
        incrementConnectionAttempts();
        return undefined;
      }
      try {
        await createSession({ floorId, eventId });
        resetConnectionAttempts();
      } catch (error) {
        incrementConnectionAttempts();
        const errorMessage = error instanceof Error ? error.message : "unknown";
        logger.error(
          `[userPresence] could not update session: ${errorMessage}`,
        );
      }

      return undefined;
    }, USER_IN_EVENT_INTERVAL_MS);

    return () => {
      window.clearInterval(timerInterval);
    };
  }, [
    eventId,
    floorId,
    tableId,
    createSession,
    incrementConnectionAttempts,
    resetConnectionAttempts,
    disconnect,
  ]);

  const userPositionedInEvent = Boolean(eventId && floorId && tableId);
  const prevUserPositionedInEvent = usePrevious(userPositionedInEvent);

  useEffect(() => {
    if (!userPositionedInEvent && prevUserPositionedInEvent && !tableId) {
      disconnectionTimeoutRef.current = window.setTimeout(() => {
        logger.info(
          "[EventUserPresenceTracker] Disconnecting as table is lost within event",
          {
            eventId,
            floorId,
            tableId,
          },
        );
        disconnect();
      }, DISCONNECT_TIMEOUT_MS);
    }

    if (userPositionedInEvent && !prevUserPositionedInEvent) {
      window.clearTimeout(disconnectionTimeoutRef.current);
    }
  }, [
    userPositionedInEvent,
    prevUserPositionedInEvent,
    eventId,
    floorId,
    tableId,
    disconnect,
  ]);

  useEffect(
    () => () => {
      if (disconnectionTimeoutRef.current) {
        window.clearTimeout(disconnectionTimeoutRef.current);
      }
    },
    [disconnectionTimeoutRef],
  );

  return null;
};

export default EventUserPresenceTracker;
